import {DOMSH} from "lib/commons/xml/domsh";
import {IReg, IUiEnv, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IWedEditorMain, OWedEditorMainConfig, WedMgr} from "back/edit/wed/wedEditor";
import {Action, IAction, IActionToggle} from "lib/commons/actions";
import {Button, ButtonActions, ButtonToggle} from "back/commons/widgets/buttons";
import {ISchemaSearch, ISkSearchAnnot, isSkSearchAnnot, SkCssSelectorSearch, SkSearchCommentAnnot, SkTextSearch, SkTextSearchInAtts, SkXPathSearch} from "lib/edit/schema/schemaSearch";
import {LANG} from "lib/commons/lang";
import {ISkAnnot} from "lib/edit/schema/schemaAnnots";
import {POPUP} from "back/commons/widgets/popups";
import {IXAddr, IXAddrRange, XA} from "lib/commons/xml/xAddr";
import {EWedletEditMode, findWedEditor, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {IDocBatch} from "lib/edit/docHolder";
import {XmlBatch} from "lib/edit/ot/xmlHouse";


export interface OWedEditorSearchBarConfig extends OWedEditorMainConfig {
	/** Désactive cette searchBar. */
	hideSearchBar?: boolean;
}

export interface IWedEditorSearchBar extends IWedEditorMain {
	searchBar?: WedSearchBar;
}

/** Agrégation pour le IWedEditor ajoutant la searchBar. */
export function AgSearchBarEditor(cls: any): any {
	const proto = cls.prototype as IWedEditorSearchBar;
	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorSearchBar, config: OWedEditorSearchBarConfig) {
		superInit.call(this, config);
		if (!config.hideSearchBar) {
			this.searchBar = new WedSearchBar().init(this, config);
			this.wedMgr.accelKeyMgr.addAccelKey("f", 'accel', ToggleSearchBar.SINGLETON);
			this.reg.addToList("actions:wed:commonbar:end", "searchBar", 1, ShowSearchBar.SINGLETON, 1);
			this.wedMgr.listeners.on("redrawAtEnd", coordSearch);
			this.wedMgr.listeners.on("shown", coordSearch);
		}
		return this;
	};
	return cls;
}

function coordSearch(wedMgr: WedMgr) {
	const search = (wedMgr.wedEditor.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord?.getParams();
	if (search) (wedMgr.wedEditor as IWedEditorSearchBar).searchBar.setSearch(search);
}

/** Action d'affichage ou masquage de la searchBar. */
export class ToggleSearchBar extends Action<IWedEditorSearchBar> implements IActionToggle<IWedEditorSearchBar> {
	static SINGLETON = new ToggleSearchBar();

	constructor(id?: string) {
		super('search' || id);
		this._label = "Parcourir, remplacer...";
		this._icon = "/@skin@/edit/wed/search/search.svg";
	}

	isToggle(): this is IActionToggle<IWedEditorSearchBar> {return true}

	getDatas(api: 'toggle', ctx: IWedEditorSearchBar): boolean | null {
		return ctx.searchBar && !ctx.searchBar.hidden;
	}

	execute(ctx: IWedEditorSearchBar, ev?: Event) {
		if (ctx.searchBar.hidden) {
			ctx.searchBar.show();
		} else {
			ctx.searchBar.close();
		}
	}
}

/** Action pour bouton masqué si la searchBar est déjà visible. */
export class ShowSearchBar extends ToggleSearchBar {
	static SINGLETON = new ShowSearchBar();

	isToggle(): this is IActionToggle<IWedEditorSearchBar> {return false}

	isVisible(ctx: IWedEditorSearchBar): boolean {
		if (!ctx.searchBar.hidden) return false;
		return super.isVisible(ctx);
	}
}

/**
 * Widget principal de la searchBar.
 */
export class WedSearchBar extends HTMLElement {
	editor: IWedEditorSearchBar;

	searchActions: ISearchBarAction[];

	protected selector: ButtonActions<WedSearchBar>;

	protected rootPanels: HTMLElement;

	currentPanel: ISearchBarPanel | null;

	constructor() {
		super();
		this.id = "searchBar";
		this.hidden = true;
	}

	init(editor: IWedEditorSearchBar, config: OWedEditorSearchBarConfig): this {
		this.editor = editor;
		return this;
	}

	connectedCallback() {
		if (!this.shadowRoot) this.lazyInit();
		this.refresh();
	}

	protected lazyInit() {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const reg = REG.findReg(this);
		reg.installSkin(this.localName, sr);

		this.searchActions = reg.getList("wed.searchBar.actions");

		this.selector = sr.appendChild(new ButtonActions().initialize({
			reg,
			actions: this.searchActions,
			actionContext: this
		}) as ButtonActions<WedSearchBar>);
		this.rootPanels = sr.appendChild(<div/>);
		sr.appendChild(<c-button icon="/@skin@/commons/icons/close.svg" title="Fermer" onclick={this.onClose}/>);
	}

	/** Paramètres d'init d'un panel. */
	initParams: JWedSearchBarParams;

	setSearch(params: JWedSearchBarParams) {
		if (this.hidden) {
			this.editor.barLayout.showBar(this);
			this.hidden = false;
		}
		if (!this.shadowRoot) this.lazyInit();
		if (this.currentPanel?.action.getId() !== params.searchId) {
			const act = this.searchActions.find((a) => a.getId() === params.searchId);
			if (!act) return;
			try {
				this.initParams = params;
				act.execute(this);
			} finally {
				this.initParams = null;
			}
		} else {
			this.currentPanel.setSearch(params);
		}
	}

	getSearchParams(): JWedSearchBarParams | null {
		return !this.hidden && this.currentPanel ? this.currentPanel.buildSearch() : null;
	}

	close() {
		this.hidden = true;
		this.editor.barLayout.onHideBar(this);
		this.setCurrentPanel(null);
	}

	show() {
		this.hidden = false;
		this.editor.barLayout.showBar(this);
		this.refresh();
	}

	setCurrentPanel(panel: ISearchBarPanel | null) {
		if (panel && panel.parentNode !== this.rootPanels) {
			//1ère déclaration, on ajoute.
			this.rootPanels.append(panel);
			panel.hidden = true;
		}
		//On masque et déconnecte tous les autres
		const wedMgr = this.editor.wedMgr;
		for (let ch = this.rootPanels.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (DOM.setHidden(ch, ch !== panel)) {
				if (ch !== panel) (ch as ISearchBarPanel).unconnectDoc();
			}
		}
		//On affiche et connecte ce panel
		if (panel) {
			if (wedMgr.docHolderAsync) panel.connectDoc(wedMgr);
			else this.editor.wedMgr.listeners.once("redrawAtEnd", () => {panel.connectDoc(wedMgr)});
		}
		this.selector.icon = panel ? panel.action.getIcon(this) : "";
		this.currentPanel = panel;
		this.dispatchSearchChange();
	}

	dispatchSearchChange() {
		// const coord = (this.editor.reg.env as IWedSearchCoordinatorPointer).wedSearchCoord;
		// if (coord) coord.setParams(this.currentPanel?.buildSearch() || null);
	}

	getPanelFor(action: ISearchBarAction): ISearchBarPanel | null {
		for (let ch = this.rootPanels.firstElementChild; ch; ch = ch.nextElementSibling) {
			if ((ch as ISearchBarPanel).action === action) return ch as ISearchBarPanel;
		}
		return null;
	}

	protected onClose(this: Button) {
		DOMSH.findHost<WedSearchBar>(this).close();
	}

	refresh() {
		if (this.hidden) return;
		if (!this.currentPanel) {
			this.searchActions?.[0].execute(this);
		} else {
			this.currentPanel.action.execute(this); //refresh.
		}
	}
}

REG.reg.registerSkin("wed-searchbar", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 1.5em;
		min-width: 0;
		background-color: var(--bgcolor);
		font-size: var(--label-size);
	}

	[hidden] {
		display: none !important;
	}

	div {
		flex: 1;
	}
`);
customElements.define("wed-searchbar", WedSearchBar);

/** Double bouton précédent / suivant à injecter dans les panels de recherche. */
export class WedSearchBarNextPrev extends HTMLElement {

	annotFilter: (a: ISkAnnot) => boolean = isSkSearchAnnot;

	connectedCallback() {
		if (!this.shadowRoot) {
			const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
			const reg = REG.findReg(this);
			reg.installSkin(this.localName, sr);
			sr.append(
				<c-button icon="/@skin@/edit/wed/search/prev.svg" title="Précédent" onclick={this.onPrevious}/>,
				<c-button icon="/@skin@/edit/wed/search/next.svg" title="Suivant" onclick={this.onNext}/>
			);
		}
	}

	setDisabled(disabled: boolean) {
		for (const btn of this.shadowRoot.querySelectorAll<Button>("c-button")) {
			btn.disabled = disabled;
		}
	}

	protected onNext(this: Button) {
		if (!this.disabled) DOMSH.findHost<WedSearchBarNextPrev>(this).gotoAnnot(1);
	}

	protected onPrevious(this: Button) {
		if (!this.disabled) DOMSH.findHost<WedSearchBarNextPrev>(this).gotoAnnot(-1);
	}

	/** Retourne la dernière annot sélectionnée si la sel courante la pointe toujours. */
	getLastSelectedAnnot(): ISkAnnot | null {
		if (this._lastAnnot) {
			const wedMgr = findWedEditor(this).wedMgr;
			if (wedMgr.lastSkAnnots.indexOf(this._lastAnnot) >= 0) {
				//annot toujours active
				if (this.isSelMatchLastAnnot(wedMgr.getCurrentSel())) {
					//la sel courante pointe toujours cette annot
					return this._lastAnnot;
				}
			}
		}
		return null;
	}

	async gotoAnnot(offset: number) {
		const wedMgr = findWedEditor(this).wedMgr;
		const annots = wedMgr.lastSkAnnots;
		const countAnnots = annots.length;
		let foundAnnot: ISkAnnot = null;
		let cycled = false;
		if (annots && countAnnots > 0) {
			const sel = wedMgr.getCurrentSel();
			let startFrom = 0;
			if (sel) {
				let startXa: IXAddr;
				if (this.isSelMatchLastAnnot(sel)) {
					//Même sel que le dernier focus d'annot.
					//On reprend le start de l'annot pas de la sel réelle qui peut être décalée.
					sel.start = this._lastAnnot.start;
				}
				startXa = offset < 0 ? sel.start : !sel.startInText ? XA.append(sel.start, 0) : sel.end || sel.start;
				startFrom = countAnnots;
				for (let i = 0; i < countAnnots; i++) {
					const annot = annots[i];
					if (this.annotFilter(annot) && !XA.isAfter(startXa, annot.start)) {
						//startXa <= anchor
						//si on remonte, on ne doit pas être un ancêtre.
						startFrom = i;
						break;
					}
				}
			}
			for (let i = offset < 0 ? startFrom - 1 : startFrom; offset < 0 ? i >= 0 : i < countAnnots; i = i + offset) {
				const a = annots[i];
				if (this.annotFilter(a)) {
					foundAnnot = a;
					await WEDLET.focusAnnot(wedMgr, foundAnnot);
					break;
				}
			}
			if (!foundAnnot) {
				cycled = true;
				for (let i = offset < 0 ? countAnnots - 1 : 0; offset < 0 ? i >= startFrom : i < startFrom; i = i + offset) {
					const a = annots[i];
					if (this.annotFilter(a)) {
						foundAnnot = a;
						await WEDLET.focusAnnot(wedMgr, foundAnnot);
						break;
					}
				}
			}
		}
		if (!foundAnnot) {
			this._lastAnnotSelStart = null;
			this._lastAnnot = null;
			POPUP.showNotif("Aucune occurrence trouvée", this, {autoHide: 500, noCloseBtn: true}, {posFrom: wedMgr.wedEditor.rootNode, fromX: "middle", targetX: "middle", fromY: "middle"});
		} else {
			//Mémoire du start de cette dernière annot car la sel réelle dans un phraseTag du texte pour une annot dans ses metas ne permet pas de
			//gérer la navigation correctement par simple ordonnacnement de la sel courante et du start de l'annot.
			//XXX algo imparfait car cette mémoire n'est pas ajustée avec les edits concurrents. a voir...
			this._lastAnnotSelStart = wedMgr.getCurrentSel()?.start;
			this._lastAnnot = foundAnnot;
			if (cycled) {
				if (offset > 0) {
					POPUP.showNotif("Fin du contenu atteint, poursuite au début", this, {autoHide: 900, noCloseBtn: true, classes: "hover"}, {
						posFrom: wedMgr.wedEditor.rootNode,
						fromX: "middle",
						targetX: "middle",
						fromY: "bottom",
						targetY: "bottom"
					});
				} else {
					POPUP.showNotif("Début du contenu atteint, poursuite par la fin", this, {autoHide: 900, noCloseBtn: true, classes: "hover"}, {posFrom: wedMgr.wedEditor.rootNode, fromX: "middle", targetX: "middle", fromY: "top"});
				}
			}
		}
	}

	protected _lastAnnotSelStart: IXAddr;
	protected _lastAnnot: ISkAnnot;

	isSelMatchLastAnnot(currentSel: IXAddrRange): boolean {
		//FIXME this._lastAnnotSelStart devrait être ajusté avec les edits concurrents.
		return this._lastAnnotSelStart && XA.isEquals(this._lastAnnotSelStart, currentSel.start)
	}
}

REG.reg.registerSkin("wed-searchbar-nextprev", 1, /* language=CSS */ `
	:host {
		display: flex;
	}
`);
customElements.define("wed-searchbar-nextprev", WedSearchBarNextPrev);


/** Action responsable de l'affichage d'un des panels de la searchBar. */
interface ISearchBarAction extends IAction<WedSearchBar> {
	maxResults: number
}

/**
 * Impl de base pour l'action de choix du panel de search.
 */
export abstract class SearchBarPanelAction extends Action<WedSearchBar> implements ISearchBarAction {

	maxResults: number;

	isEnabled(ctx: WedSearchBar): boolean {
		if (ctx.currentPanel?.action === this) return false;
		return super.isEnabled(ctx);
	}

	execute(ctx: WedSearchBar, ev?: Event) {
		let panel = ctx.getPanelFor(this);
		if (!panel) panel = this.buildPanel(ctx);
		if (ctx.initParams) panel.setSearch(ctx.initParams);
		ctx.setCurrentPanel(panel);
	}

	abstract buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel
}

/** Données pour initialiser un panneau de recherche. Les autres paramètres sont dépendants de chaque type de panneau de recherche. */
export interface JWedSearchBarParams {
	/** Id du ISearchBarAction cible */
	searchId: string
}


/** Panel construit par ISearchBarAction et affecté à la searchBar via WedSearchBar.setCurrentPanel(). */
export interface ISearchBarPanel extends HTMLElement {

	/** Action à l'origine de la création de ce panel.*/
	action: ISearchBarAction

	/** Connecte l'éditeur à ce panneau de recherche. Injecte alors les objets recherchés. */
	connectDoc(wedMgr: WedMgr): void

	/** Déconnecte le panneau de recherche du document.*/
	unconnectDoc(): void

	/** Modifie les paramètres de recherche. */
	setSearch(params: JWedSearchBarParams): void

	/** Construit un objet */
	buildSearch(): JWedSearchBarParams
}


/** Classe de base pour les panels de barre de recherche. */
export abstract class SearchPanelBase extends HTMLElement implements ISearchBarPanel {
	action: ISearchBarAction;
	wedMgr: WedMgr;
	result: HTMLElement;
	nav: WedSearchBarNextPrev;
	searchBar?: HTMLElement;
	replaceBtn?: ButtonToggle;
	replaceBar?: HTMLElement;
	reg: IReg<IUiEnv>;

	protected _initPanel(action: ISearchBarAction, bar: WedSearchBar, withReplacePanel: boolean) {
		this.action = action;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg = REG.findReg(bar);
		this.reg.installSkin("wed-searchbar-base", sr);
		this.result = <span id="result"/> as HTMLElement;
		this.nav = new WedSearchBarNextPrev();
		if (withReplacePanel) {
			this.classList.add("double");
			this.replaceBtn = <ButtonToggle icon="/@skin@/edit/wed/search/replace.svg" title="Remplacer..." onclick={this.toggleReplace}/> as ButtonToggle;
			this.searchBar = <div id="search"/>;
			sr.append(
				<div id="bars">
					{this.searchBar}
					{this.replaceBar}
				</div>,
				this.replaceBtn
			);
			this._refreshOnReadOnlyChange = this.refreshOnReadOnlyChange.bind(this);
		} else {
			this.classList.add("single");
		}
		this._asyncSkAnnots = this.asyncSkAnnots.bind(this);
	}

	connectDoc(wedMgr: WedMgr): void {
		this.wedMgr = wedMgr;
		wedMgr.listeners.on("asyncSkAnnots", this._asyncSkAnnots);
		if (this._refreshOnReadOnlyChange) {
			wedMgr.listeners.on("readOnlyChangeAfter", this._refreshOnReadOnlyChange);
			this.refreshOnReadOnlyChange();
		}
		this.execSearch();
	}

	unconnectDoc(): void {
		if (this.wedMgr) {
			this.wedMgr.listeners.removeListener("asyncSkAnnots", this._asyncSkAnnots);
			if (this._refreshOnReadOnlyChange) this.wedMgr.listeners.removeListener("readOnlyChangeAfter", this._refreshOnReadOnlyChange);
			this.wedMgr.docHolder.setSkSearch(null);
			this.wedMgr = null;
		}
	}

	abstract setSearch(params: JWedSearchBarParams): void;

	buildSearch(): JWedSearchBarParams {
		return {searchId: this.action.getId()}
	}

	get replaceBarShown(): boolean {return this.replaceBtn?.toggleOn || false}

	showReplaceBar(show: boolean) {
		if (show) {
			if (!this.replaceBar) {
				this.replaceBar = <div id="replace"/>;
				this.searchBar.insertAdjacentElement("afterend", this.replaceBar);
				this.buildReplaceBar();
			}
			this.replaceBar.hidden = false;
			this.replaceBtn.toggleOn = true;
		} else {
			if (this.replaceBar) this.replaceBar.hidden = true;
			this.replaceBtn.toggleOn = false;
		}
	}

	toggleReplace(this: ButtonToggle) {
		if (this.disabled) return;
		const me = DOMSH.findHost<SearchPanelBase>(this);
		me.showReplaceBar(!this.toggleOn);
	}

	/** A surcharger par les bar qui ont une fct de remplacement. */
	protected buildReplaceBar() {}

	protected lastCount: number | null = null;

	updateCount(count: number | null) {
		this.lastCount = count;
		switch (count) {
		case null:
			DOM.setTextContent(this.result, null);
			break;
		case 0:
			DOM.setTextContent(this.result, "➔ Aucune occurrence trouvée");
			break;
		case 1:
			DOM.setTextContent(this.result, "➔ 1 occurrence trouvée");
			break;
		default:
			DOM.setTextContent(this.result, count > this.action.maxResults ? `➔ Plus de ${this.action.maxResults} occurrences trouvées` : `➔ ${count} occurrences trouvées`);
			break;
		}
	}

	abstract execSearch(): void;

	_asyncSkAnnots: (wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void);

	asyncSkAnnots(wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline): void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void) {
		let c = 0;
		for (const a of annots) if (isSkSearchAnnot(a)) c++;
		if (this.lastCount > this.action.maxResults && c < this.action.maxResults) {
			//On est peut-etre repassé sous la limite max, il faut relancer une recherche exhaustive.
			this.updateCount(c);
			this.execSearch();
		} else {
			this.updateCount(c);
		}
	}

	_refreshOnReadOnlyChange: () => void;

	refreshOnReadOnlyChange() {
		if (this.wedMgr.readOnly) {
			this.replaceBtn.disabled = true;
			this.showReplaceBar(false);
		} else {
			this.replaceBtn.disabled = false;
		}
	}

}


REG.reg.registerSkin("wed-searchbar-base", 1, /* language=CSS */ `
	:host(.single),
	#search,
	#replace {
		display: flex;
		flex-wrap: wrap;
		padding: 2px;
		align-items: center;
	}

	:host(.double) {
		display: flex;
	}

	[hidden] {
		display: none !important;
	}

	#bars {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	#replace {
		border-top: 1px solid var(--border-color);
	}

	input {
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		border: 1px solid var(--border-color);
	}

	input:focus {
		outline: var(--focus-outline);
	}

	label {
		display: flex;
		align-items: center;
		user-select: none;
		margin-inline-start: .5em;
	}

	.options {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
	}

	#result {
		margin: 0 .5em;
		color: var(--alt1-color);
		flex: 1 0 auto;
		text-align: center;
	}

	c-button.border {
		border: 1px solid var(--border-color);
		border-radius: 3px;
		margin-inline: .3em;
	}
`);


/**
 *
 */
export class TextSearchAction extends SearchBarPanelAction {

	searchInAtts = false;

	constructor(id?: string, maxResults?: number) {
		super(id || 'text');
		this.maxResults = maxResults || 300;
		this._icon = "/@skin@/edit/wed/search/textSearch.svg";
		this._label = "Chercher dans le texte";
	}

	setSearchInAtts(v: boolean): this {
		this.searchInAtts = v;
		return this;
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new TextSearchPanel().initPanel(this, ctx);
	}
}

REG.reg.addToList("wed.searchBar.actions", "text", 1, new TextSearchAction());

export interface JTextWedSearchBarParams extends JWedSearchBarParams {
	text?: string
	matchCase?: boolean
	wholeWord?: boolean
	repBy?: string
}

/** */
class TextSearchPanel extends SearchPanelBase {
	action: TextSearchAction;

	input: HTMLInputElement;
	matchCase: HTMLInputElement;
	wholeWord: HTMLInputElement;

	inputRepl: HTMLInputElement;

	initPanel(action: TextSearchAction, bar: WedSearchBar): this {
		this._initPanel(action, bar, true);
		this.input = <input type="search" oninput={this.onChange}/> as HTMLInputElement;
		this.matchCase = <input type="checkbox" onchange={this.onChange}/> as HTMLInputElement;
		this.wholeWord = <input type="checkbox" onchange={this.onChange}/> as HTMLInputElement;
		this.searchBar.append(
			this.input,
			this.nav,
			<div class="options">
				<label>{this.matchCase}Respecter la casse</label>
				<label>{this.wholeWord}Mot entier</label>
			</div>,
			this.result
		);
		return this;
	}

	setSearch(params: JTextWedSearchBarParams): void {
		if (params.text != null) this.input.value = params.text;
		if (typeof params.matchCase === "boolean") this.matchCase.checked = params.matchCase;
		if (typeof params.wholeWord === "boolean") this.wholeWord.checked = params.wholeWord;
		this.execSearch();
		if (params.repBy !== undefined) {
			this.showReplaceBar(true);
			this.inputRepl.value = params.repBy;
		}
	}

	buildSearch(): JTextWedSearchBarParams {
		return {
			searchId: this.action.getId(),
			text: this.input.value,
			matchCase: this.matchCase.checked,
			wholeWord: this.wholeWord.checked,
			repBy: this.replaceBarShown && this.inputRepl ? this.inputRepl.value : undefined
		};
	}

	protected onChange(this: HTMLInputElement) {
		const me = DOMSH.findHost<TextSearchPanel>(this);
		me.execSearch();
		DOMSH.findHost<WedSearchBar>(me).dispatchSearchChange();
	}

	protected buildReplaceBar() {
		this.inputRepl = <input type="search"/> as HTMLInputElement;
		this.replaceBar.append(
			this.inputRepl,
			<Button class="border" label="Remplacer" onclick={this.onReplace}/>,
			<Button class="border" label="Tout remplacer" onclick={this.onReplaceAll}/>
		);
	}

	protected onReplace(this: HTMLInputElement) {
		const me = DOMSH.findHost<TextSearchPanel>(this);
		const annot = me.nav.getLastSelectedAnnot();
		if (annot instanceof SkSearchCommentAnnot) {
			me.warnNoReplaceInCmt();
			return;
		}
		if (annot) {
			const wedMgr = me.wedMgr;
			const start = XA.freeze(annot.start);
			const len = annot.len;
			const startWedlet = WEDLET.findWedlet(wedMgr.rootWedlet, start, {lastAncestorIfNone: true});
			if (startWedlet && WEDLET.resolveEditMode(startWedlet) === EWedletEditMode.write) {
				const batch = wedMgr.docHolder.newBatch(me.wedMgr.getCurrentSel());
				me.replaceText(batch, start, len, me.inputRepl.value);
				batch.setSelAfterSeq(start, me.inputRepl.value.length);
				batch.doBatch();
			} else {
				POPUP.showNotifForbidden("Contenu non modifiable", this, null, {viewPortX: "middle", viewPortY: "1/3"});
			}
		}
		me.nav.gotoAnnot(1);
	}

	protected onReplaceAll(this: HTMLInputElement) {
		const me = DOMSH.findHost<TextSearchPanel>(this);
		const wedMgr = me.wedMgr;
		const batch = wedMgr.docHolder.newBatch(wedMgr.getCurrentSel());
		const rep = me.inputRepl.value;
		const annots = wedMgr.lastSkAnnots;
		let lastAn: ISkSearchAnnot;
		let inComment = false;
		for (let i = annots.length - 1; i >= 0; i--) {
			const an: ISkAnnot = annots[i];
			if (isSkSearchAnnot(an)) {
				if (an instanceof SkSearchCommentAnnot) {
					inComment = true;
				} else {
					const start = XA.freeze(an.start);
					const startWedlet = WEDLET.findWedlet(wedMgr.rootWedlet, start, {lastAncestorIfNone: true});
					//FIXME perms configuré sur wedlets, mais wedlets peut être non construit, passer perms sur schéma...
					if (startWedlet && WEDLET.resolveEditMode(startWedlet) === EWedletEditMode.write) me.replaceText(batch, start, an.len, rep);
					if (!lastAn) lastAn = an;
				}
			}
		}
		if (lastAn) {
			batch.setSelAfterSeq(lastAn.start, rep.length);
			batch.doBatch();
		} else {
			me.nav.gotoAnnot(1);
		}
		if (inComment) me.warnNoReplaceInCmt();
	}

	protected warnNoReplaceInCmt() {
		POPUP.showNotifForbidden("Éditez les commentaires pour effectuer les remplacements", this, null,
			{posFrom: this.wedMgr.wedEditor, fromX: "middle", targetX: "middle", fromY: "middle", targetY: "middle"});
	}

	protected replaceText(batch: IDocBatch & XmlBatch, start: IXAddr, len: number, rep: string) {
		batch.deleteSequence(start, len);
		if (rep) batch.insertText(start, rep);
	}


	execSearch() {
		if (!this.wedMgr) return;
		const v = this.rawValue();
		if (!v) {
			this.wedMgr.docHolder.setSkSearch(null);
			this.updateCount(null);
		} else {
			if (this.wedMgr.docHolder.setSkSearch(this.newSkTextSearch(v)).length === 0) {
				this.updateCount(0);
			}
		}
	}

	protected newSkTextSearch(value: string): ISchemaSearch {
		if (this.action.searchInAtts) {
			return new SkTextSearchInAtts(this.buildRegexp(value), this.action.maxResults);
		} else {
			return new SkTextSearch(this.buildRegexp(value), this.action.maxResults);
		}
	}

	/** pour surcharge custom */
	protected rawValue(): string {
		return this.input.value; //.trim();
	}

	/** pour surcharge custom */
	protected buildRegexp(rawValue: string): RegExp {
		let r = LANG.escape4RegexpFuzzy(rawValue);
		//NOTE : search "wholeWord" à mettre en cohérence avec {@link com.scenari.m.bdp.module.fulltext.ModuleFullTextXmlTransform#makePattern}
		if (this.wholeWord.checked) r = '(?<=^|\\W)' + r + '(?=$|\\W)'; // \b échoue en unicode https://stackoverflow.com/questions/10590098/javascript-regexp-word-boundaries-unicode-characters
		const flags = this.matchCase.checked ? "g" : "gi";
		return new RegExp(r, flags);
	}

	connectDoc(wedMgr: WedMgr): void {
		super.connectDoc(wedMgr);
		this.input.focus();
	}

	asyncSkAnnots(wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline): void | ((wedMgr: WedMgr, annots: ISkAnnot[], deadline: RequestIdleDeadline) => void) {
		if (!this.rawValue()) return;
		super.asyncSkAnnots(wedMgr, annots, deadline);
	}

}

customElements.define("wed-searchbar-text", TextSearchPanel);


/** Panel générique sans option dédiée. */
class NoOptionsSearchPanel extends SearchPanelBase {

	skSearchMk: () => ISchemaSearch;

	initPanel(action: ISearchBarAction, bar: WedSearchBar): this {
		this._initPanel(action, bar, false);
		this.shadowRoot.append(
			<span>{action.getLabel(bar)}</span>,
			this.nav,
			this.result
		);
		return this;
	}

	initSearch(skSearchMk: () => ISchemaSearch): this {
		this.skSearchMk = skSearchMk
		return this;
	}

	setSearch(params: JWedSearchBarParams): void {
		this.execSearch();
	}

	execSearch() {
		if (!this.wedMgr) return;
		if (this.wedMgr.docHolder.setSkSearch(this.skSearchMk()).length === 0) {
			this.updateCount(0);
		}
	}
}

customElements.define("wed-searchbar-nooptions", NoOptionsSearchPanel);

/**
 * Parcours Xpath prédéfini sur les éléments xml du document
 */
export class FixedXPathSearchAction extends SearchBarPanelAction {

	constructor(id: string, public xpath: XPathExpression, public annotLabel: string, maxResults?: number) {
		super(id);
		this.maxResults = maxResults || 300;
		//this._icon = "/@skin@/edit/wed/search/textSearch.svg"; todo textXml.svg
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new NoOptionsSearchPanel().initPanel(this, ctx).initSearch(() => new SkXPathSearch(this.xpath, this.annotLabel, this.maxResults));
	}
}

/**
 * Parcours Regexp prédéfini sur les textes du document
 */
export class FixedCssSelectorSearchAction extends SearchBarPanelAction {

	constructor(id: string, public selector: string, public annotLabel: string, maxResults?: number) {
		super(id);
		this.maxResults = maxResults || 300;
		//this._icon = "/@skin@/edit/wed/search/textSearch.svg";
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new NoOptionsSearchPanel().initPanel(this, ctx).initSearch(() => new SkCssSelectorSearch(this.selector, this.annotLabel, this.maxResults));
	}
}

/**
 * Parcours Regexp prédéfini sur les textes du document
 */
export class FixedTextSearchAction extends SearchBarPanelAction {

	constructor(id: string, public pattern: RegExp, maxResults?: number) {
		super(id);
		this.maxResults = maxResults || 300;
		//this._icon = "/@skin@/edit/wed/search/textSearch.svg";
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new NoOptionsSearchPanel().initPanel(this, ctx).initSearch(() => new SkTextSearch(this.pattern, this.maxResults));
	}
}

//
// REG.reg.addToList("wed.searchBar.actions", "repriseError", REG.LEVELAUTH_MODEL,
// 	new FixedXPathSearchAction(
// 		"repriseError",
// 		document.createExpression("//paon:text//sc:para/sc:inlineStyle[@role='error']", (p) => p == "paon" ? "editadapt.fr:paon" : p == "sc" ? "http://www.utc.fr/ics/scenari/v3/core" : null),
// 		"Erreur à la reprise"
// 	).setLabel("Parcourir les erreurs à la reprise").setIcon(null)
//  , 10
// );
//
// REG.r.addToList("wed.searchBar.actions", "bridgehead", 101,
// 	new FixedCssSelectorSearchAction(
// 		"bridgehead",
// 		"flow > bridgehead",
// 		"Titre flottant"
// 	).setLabel("Parcourir les titres flottants").setIcon(null) //todo icon
// 	, 10
// );
//
// REG.reg.addToList("wed.searchBar.actions", "roman", REG.LEVELAUTH_MODEL,
// 	new FixedTextSearchAction(
// 		"roman",
// 		/(?<=^|\W)[IVXLCDM]+(?=$|\W)/g
// 	).setLabel("Parcourir les chiffres romans")
//  , 10
// );

/**
 * Objet à instancier et à associer à un IReg.env
 * pour coordonner la recherche par défaut à afficher lors
 * de l'affichage d'un éditeur.
 */
export class WedSearchCoordinator {

	protected params?: JWedSearchBarParams;

	/** Limite de validité de ce params (cas où une ouverture planifiée a échouée). */
	protected deadline: number;

	getParams(): JWedSearchBarParams | null {
		let p = this.params;
		if (p) {
			if (this.deadline > 0 && Date.now() > this.deadline) {
				p = null;
				this.deadline = 0;
			}
			this.params = null;
		}
		return p;
	}

	setParamsOnce(params: JWedSearchBarParams) {
		this.params = params;
		this.deadline = Date.now() + 3000;
	}
}

export interface IWedSearchCoordinatorPointer {
	wedSearchCoord?: WedSearchCoordinator
}